import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { DropIcon } from "../Icons";
import TableWithPagination from "../Tables/TableWithPagination";

const FileUploader = ({
  selectedFiles = [],
  onFilesChange,
  multiple = true,
  acceptedTypes = "*/*",
  "data-testid": dataTestId = "file-uploader",
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    if (files.length > 0) {
      const newFiles = files.map((file, index) => ({
        id: Date.now() + index, // Unique ID
        name: file.name,
        realFileName: file.name, // Original file name
        size: file.size,
        type: file.type,
        file: file, // Original file object
        lastModified: file.lastModified,
        fileInfoSeq: null, // Not uploaded yet
      }));

      const updatedFiles = multiple ? [...selectedFiles, ...newFiles] : newFiles;
      onFilesChange(updatedFiles);
    }

    // Reset input to allow selecting the same file again
    event.target.value = "";
  };

  const handleDeleteFile = (fileToDelete) => {
    const updatedFiles = selectedFiles.filter((file) => file.id !== fileToDelete.id);
    onFilesChange(updatedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length > 0) {
      const newFiles = files.map((file, index) => ({
        id: Date.now() + index, // Unique ID
        name: file.name,
        realFileName: file.name, // Original file name
        size: file.size,
        type: file.type,
        file: file, // Original file object
        lastModified: file.lastModified,
        fileInfoSeq: null, // Not uploaded yet
      }));

      const updatedFiles = multiple ? [...selectedFiles, ...newFiles] : newFiles;
      onFilesChange(updatedFiles);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Table config cho files
  const fileTableColumns = ["name", "type", "size"];
  const fileTableColumnConfig = {
    name: {
      style: { width: "50%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.file-name-documents"),
      customRender: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className={`h-8 w-8 rounded ${colors.bgInput} flex items-center justify-center`}>
            <svg className={`h-4 w-4 ${colors.iconMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <span className={`${colors.textPrimary} font-medium`}>{row.realFileName || row.name}</span>
        </div>
      ),
    },
    type: {
      style: { width: "20%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.type"),
      customRender: (value, row) => (
        <span className={`${colors.textSecondary} text-sm`}>{row.type || t("common.unknown")}</span>
      ),
    },
    size: {
      style: { width: "20%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.size"),
      customRender: (value, row) => (
        <span className={`${colors.textSecondary} text-sm`}>{formatFileSize(row.size)}</span>
      ),
    },
  };

  return (
    <div data-testid={dataTestId}>
      {/* File list table */}
      {selectedFiles.length > 0 && (
        <div className="mb-4" data-testid={`${dataTestId}-files-table`}>
          <TableWithPagination
            dataTestId={`${dataTestId}-table`}
            columns={fileTableColumns}
            columnConfig={fileTableColumnConfig}
            data={selectedFiles}
            currentPage={1}
            setCurrentPage={() => {}} // No pagination needed
            loading={false}
            rowKey="id"
            totalPages={1}
            totalData={selectedFiles.length}
            maxRowsPerPage={selectedFiles.length}
            hasCheckbox={false}
            hasDeleteColumn={true}
            onDeleteRow={handleDeleteFile}
            showPagination={false}
            onItemsPerPageChange={() => {}} // Not needed
          />
        </div>
      )}

      {/* Drop zone */}
      <div
        data-testid={`${dataTestId}-drop-zone`}
        className={`${colors.bgDropFiles} border-1 border-dashed ${colors.borderActiveDashed} rounded-lg p-1 text-center transition-all duration-300 ${
          isDragOver ? `${colors.borderActiveDashed} ${colors.bgDropFilesActive}` : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex justify-center not-first-of-type:items-center p-1">
          <div className="flex items-center justify-center">
            <DropIcon className={`h-9 w-9 ${colors.textActive}`} />
          </div>
          <div className="flex items-center">
            <p className={`text-sm font-medium ${colors.textSubTitle} me-2`}>{t("common.drop-files-or")}</p>
            <button
              onClick={handleBrowseClick}
              data-testid={`${dataTestId}-browse-button`}
              className={`px-4 py-2 text-sm font-semibold ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton} rounded-lg transition-all duration-1000 ease-in-out`}
            >
              {t("common.browse")}
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={acceptedTypes}
        onChange={handleFileSelect}
        data-testid={`${dataTestId}-file-input`}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default FileUploader;
